import { z } from 'zod';

import { apiGet, apiPost } from '../client';
import { BillingCycleSchema, PlanSchema } from '../schemas';

/**
 * El pago del plan de Dale (/pago). The owner reads what they owe and how
 * they can pay, starts a payment, and checks back after a provider page.
 * Parsed tolerantly: a way to pay this build does not know is dropped, not
 * a reason to show nothing.
 */

export const BILLING_QUERY_KEY = ['billing'];

export const CheckoutMethodIdSchema = z.enum(['tarjeta', 'paypal', 'yappy', 'transferencia']);
export type CheckoutMethodId = z.infer<typeof CheckoutMethodIdSchema>;
/** Every way /pago can list; Stripe has its own endpoints (`stripe.ts`), the rest start at /billing/checkout. */
export type OfferedMethodId = 'stripe' | CheckoutMethodId;

export const PaymentStatusSchema = z.enum(['pendiente', 'reportado', 'confirmado', 'fallido', 'rechazado']);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

const BankAccountSchema = z.object({
  bank: z.string(),
  holder: z.string(),
  accountType: z.string(),
  accountNumber: z.string(),
  taxId: z.string().optional(),
  note: z.string().optional(),
});
export type BankAccount = z.infer<typeof BankAccountSchema>;

const OfferedMethodSchema = z.discriminatedUnion('id', [
  z.object({ id: z.literal('stripe') }),
  z.object({ id: z.literal('tarjeta') }),
  z.object({ id: z.literal('paypal') }),
  z.object({ id: z.literal('yappy'), holder: z.string(), directory: z.string().optional(), phone: z.string().optional() }),
  z.object({ id: z.literal('transferencia'), accounts: z.array(BankAccountSchema) }),
]);
export type OfferedMethod = z.infer<typeof OfferedMethodSchema>;

const MethodListSchema = z.array(z.unknown()).transform((items) =>
  items.flatMap((item) => {
    const parsed = OfferedMethodSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  }),
);

export const PaymentAttemptSchema = z.object({
  id: z.string(),
  method: z.string(),
  status: PaymentStatusSchema,
  amountCents: z.number().int(),
  plan: PlanSchema.nullable(),
  billingCycle: BillingCycleSchema.nullable(),
  monthsCovered: z.number().int(),
  periodMonth: z.string(),
  reference: z.string().nullable(),
  statusNote: z.string().nullable(),
  createdAt: z.string(),
});
export type PaymentAttempt = z.infer<typeof PaymentAttemptSchema>;

export const CheckoutQuoteSchema = z.object({
  plan: PlanSchema,
  billingCycle: BillingCycleSchema,
  monthlyPriceCents: z.number().int(),
  amountCents: z.number().int(),
  monthsCovered: z.number().int(),
  savingsCents: z.number().int(),
});
export type CheckoutQuote = z.infer<typeof CheckoutQuoteSchema>;

export const BillingViewSchema = z.object({
  businessName: z.string(),
  country: z.enum(['PA', 'VE']),
  quote: CheckoutQuoteSchema.nullable(),
  methods: MethodListSchema,
  coverage: z.object({ status: z.enum(['al_dia', 'atrasado']), lastCoveredMonth: z.string().nullable() }),
  latest: PaymentAttemptSchema.nullable(),
  /** Paid months no longer cover the plan on file (it went up after they were paid); Dale settles the difference. */
  needsAdjustment: z.boolean().optional(),
  /** The free trial's end; nothing is due while it runs. Absent from an older server. */
  trialEndsAt: z.string().nullable().optional(),
});
export type BillingView = z.infer<typeof BillingViewSchema>;

const BillingViewResponseSchema = z.object({ data: BillingViewSchema });
const CheckoutStartResponseSchema = z.object({
  data: z.object({ payment: PaymentAttemptSchema, checkoutUrl: z.string().url().nullable() }),
});
const PaymentAttemptResponseSchema = z.object({ data: PaymentAttemptSchema });

export async function getBilling(): Promise<BillingView> {
  return (await apiGet('/v1/business/billing', BillingViewResponseSchema)).data;
}

export async function startCheckout(
  input: { method: CheckoutMethodId; reference?: string },
  idempotencyKey: string,
): Promise<{ payment: PaymentAttempt; checkoutUrl: string | null }> {
  return (await apiPost('/v1/business/billing/checkout', CheckoutStartResponseSchema, input, idempotencyKey)).data;
}

/** Safe to repeat: only the provider's own answer settles a payment. */
export async function verifyPayment(paymentId: string): Promise<PaymentAttempt> {
  const path = `/v1/business/billing/payments/${encodeURIComponent(paymentId)}/verify`;
  return (await apiPost(path, PaymentAttemptResponseSchema)).data;
}
